import { API_FLOW, API_FUNC, API_MAIN } from "@/constants/settings.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { error } from "@/helpers/console.ts";
import { JsonParse } from "@/helpers/json.ts";

/**
 * Concats the path to the flow api address set in settings.ts
 * This is a shorthand for API_FLOW + uri
 */
export const getFlowUri = (uri: string) => getUri(API_FLOW, uri);

/**
 * Concats the path to the api address set in settings.ts
 * This is a shorthand for API_MAIN + uri
 */
export const getApiUri = (uri: string) => getUri(API_MAIN, uri);

/**
 * Concats the path to the api address set in settings.ts
 * This is a shorthand for API_FUNC + uri
 */
export const getFuncUri = (uri: string) => getUri(API_FUNC, uri);

export const getUri = (endpoint: string | null = null, uri: string | null = null) => {
  const formattedUri = `/${uri}`.replace(/\/+/g, "/");
  return `${endpoint}${formattedUri}`;
};

export interface SimpleError {
  statusCode: number;
  message: string;
}

/**
 * Small wrapper for throwing with some sort of text
 * Return's an error that you should throw (as to make it clear that this function will throw when somebody is glancing an API call)
 * e.g. throw throwBody(...)
 * @returns Error - Contains the response body as a string OR the status text as a fallback
 */
export const throwBody = async (response: Response) => {
  let err = new Error(response.statusText);
  const text = await response.text();

  if (text.length > 0 || text !== "No error message") err = new Error(text);

  error("Throwing", err, response);

  return err;
};

/**
 * Error that preserves the HTTP status code and raw body text.
 *
 * Unlike {@link throwBody}, callers can branch on `statusCode` directly instead
 * of JSON.parsing the message (which explodes on text/plain bodies). New
 * endpoints should prefer this; existing throwBody callers are left untouched.
 */
export class HttpError extends Error {
  statusCode: number;
  bodyText: string;

  constructor(statusCode: number, bodyText: string, statusText: string) {
    super(bodyText.length > 0 ? bodyText : statusText);
    this.name = "HttpError";
    this.statusCode = statusCode;
    this.bodyText = bodyText;
  }
}

/**
 * Status-aware counterpart of {@link throwBody}.
 * e.g. throw await throwHttpError(response)
 */
export const throwHttpError = async (response: Response): Promise<HttpError> => {
  const text = await response.text();
  const err = new HttpError(response.status, text, response.statusText);

  error("Throwing", err, response);

  return err;
};

/**
 * The backend throttle filter responds 409 with a JSON body of
 * {"statusCode":429,"message":"Please wait a moment and try again..."} —
 * whereas real business 409s carry a plain-text message. This tells them apart.
 */
export const isThrottled409 = (e: unknown): boolean => {
  if (!(e instanceof HttpError) || e.statusCode !== 409) return false;
  const body = JsonParse<unknown>(e.bodyText);
  return typeof body === "object" && body !== null && (body as SimpleError).statusCode === 429;
};

/**
 * 404/410 from the review endpoints mean the hold was resolved elsewhere
 * (superseded, auto-unclaimed, another tutor acted) — refresh, never an error.
 */
export const isResolvedElsewhere = (e: unknown): boolean =>
  e instanceof HttpError && (e.statusCode === 404 || e.statusCode === 410);

/**
 * A small wrapper for a common response type from the backend
 * To access the internal type, use .data
 * TODO: Extend with fetching more data, as the total is not the length of the array, but the amount the server has
 */
export interface ArrayResponse<T> {
  total: number;
  data: Array<T>;
}

export interface VarToQuery {
  name: string;
  value: string | number | boolean | undefined | null;
}

/**
 * Wrapper around fetch that injects the bearer token and centralises 401 handling.
 *
 * Prefer this over calling fetch directly for anything authenticated — a 401 from
 * any endpoint now consistently logs us out instead of surfacing as an opaque
 * error body that each caller has to recognise for itself.
 *
 * @remarks 403 is deliberately left alone. It means "authenticated but not
 * permitted", which is not a credential problem — callers that want to bail on it
 * (e.g. the admin-only startup calls in user.ts) should check for it themselves.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const { auth, Logout } = useUserStore.getState();

  const headers = new Headers(options.headers);
  if (auth.token) {
    headers.set("Authorization", `Bearer ${auth.token}`);
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    Logout(true);
  }

  return response;
};

export const QueryFromVars = (query: Array<VarToQuery>): string => {
  if (query.length === 0) return "";
  return (
    "?" +
    query
      .filter((q) => q.value !== undefined && q.value !== null)
      .map(
        (q) =>
          // Safety: We filter undefined & null out
          `${encodeURIComponent(q.name)}=${encodeURIComponent(q.value as string | number | boolean)}`,
      )
      .join("&")
  );
};
